import { compact } from "lodash";
import { t } from "@/i18n-js/instance";
import {
  DISPLAY_STYLE_DEFAULT_COLORS,
  DISPLAY_STYLE_OPTIONS,
} from "@circle-react/components/Paywalls/Admin/PaywallsAdmin/FormModal/CheckoutTab/constants";
import { buildTransformInputValueToCurrencyAmountStrFunc } from "@circle-react/components/Paywalls/Admin/utils";
import {
  ACTION_TYPE,
  type BadgeProperty,
  type BenefitProperty,
  COUNTDOWN_TYPE,
  type PriceCallout,
  type ReducedPrice,
  type TestimonialProperty,
} from "@circle-react/types/PaywallCustomization";
import type { PaywallPrice } from "@circle-react/types/PaywallPrice";
import { isInstallmentsPrice, isOneTimePrice } from "./paywallPriceHelpers";

export const isRemainingTimeType = (input?: string | null) =>
  input === COUNTDOWN_TYPE.REMAINING_TIME;
export const isFinishAtType = (input?: string | null) =>
  input === COUNTDOWN_TYPE.FINISH_AT;

export const isRedirectToType = (input?: string | null) =>
  input === ACTION_TYPE.REDIRECT_TO;
export const isDoNothingType = (input?: string | null) =>
  input === ACTION_TYPE.DO_NOTHING;

export const REMAINING_TIME_DEFAULT_VALUE = 10;

export const skipCountdownRedirectsName = "skip_redirect";

export const PRICE_CALLOUT_DEFAULT_COLOR = "#063CFF";

// Form
export const defaultTestimonial: TestimonialProperty = {
  name: t(
    "settings.paywalls.modal.tabs.checkout.builder.testimonials.fields.name_placeholder",
  ),
  avatar_image_url: "",
  avatar_image: "",
  quote: t(
    "settings.paywalls.modal.tabs.checkout.builder.testimonials.fields.quote_placeholder",
  ),
  headline: t(
    "settings.paywalls.modal.tabs.checkout.builder.testimonials.fields.headline_placeholder",
  ),
  visible: true,
  display_style: {
    type: DISPLAY_STYLE_OPTIONS.STARS,
    color: DISPLAY_STYLE_DEFAULT_COLORS.STARS,
  },
};

export const defaultBadge: BadgeProperty = {
  background_color: "#F6F6F6",
  icon: { emoji_type: "emoji", emoji: "😀" },
  label: t(
    "settings.paywalls.modal.tabs.checkout.builder.badges.fields.label_placeholder",
  ),
  text_color: "#000000",
  visible: true,
};

export const defaultBenefit: BenefitProperty = {
  title: t(
    "settings.paywalls.modal.tabs.checkout.builder.benefits.fields.title_placeholder",
  ),
  description: t(
    "settings.paywalls.modal.tabs.checkout.builder.benefits.fields.description_placeholder",
  ),
  icon: {
    emoji_type: "emoji",
    emoji: "✔️",
  },
};

const buildReducedPriceAttributes = (
  paywallPrice: PaywallPrice,
): Pick<ReducedPrice, "amount" | "upfront_amount"> => {
  const upfrontAmount = paywallPrice.upfront_payment_amount || 0;
  const amount = paywallPrice.amount;

  if (isOneTimePrice(paywallPrice)) {
    return {
      amount,
      upfront_amount: 0,
    };
  }

  if (isInstallmentsPrice(paywallPrice)) {
    return {
      amount: amount - upfrontAmount,
      upfront_amount: upfrontAmount,
    };
  }

  return {
    amount,
    upfront_amount: upfrontAmount,
  };
};

const buildReducedPrice = (
  paywallPrice: any,
  currency?: Parameters<
    typeof buildTransformInputValueToCurrencyAmountStrFunc
  >[0],
  fields?: Array<any>,
): ReducedPrice | null => {
  if (paywallPrice?._destroy || !currency) {
    return null;
  }

  const transform = buildTransformInputValueToCurrencyAmountStrFunc(currency);

  const priceCalloutFound = (fields || []).find(
    (e: PriceCallout) =>
      e.price_id === (paywallPrice?.id || paywallPrice?.tmp_id),
  );

  const priceAttributes = buildReducedPriceAttributes(paywallPrice);

  return {
    price_id:
      priceCalloutFound?.price_id || paywallPrice?.id || paywallPrice?.tmp_id,
    amount: transform(priceCalloutFound?.amount || priceAttributes.amount) || 0,
    original_amount: transform(priceAttributes.amount),
    upfront_amount:
      transform(
        priceCalloutFound?.upfront_amount || priceAttributes.upfront_amount,
      ) || 0,
    original_upfront_amount: transform(priceAttributes.upfront_amount),
    enabled: priceCalloutFound?.enabled || false,
  };
};

const buildPriceCallout = (
  input: any,
  fields?: Array<any>,
): PriceCallout | null => {
  if (input?._destroy) {
    return null;
  }

  const priceCalloutFound = (fields || []).find(
    (e: PriceCallout) => e.price_id === (input?.id || input?.tmp_id),
  );

  if (priceCalloutFound) {
    return priceCalloutFound;
  }

  return {
    price_id: input?.id || input?.tmp_id,
    callout_text: "",
    highlighted: false,
  };
};

export const findPriceCalloutOrInitialize = (
  prices: Array<any>,
  fields: Array<any>,
) =>
  compact(
    prices.map((price: PaywallPrice) => buildPriceCallout(price, fields)),
  );

export const findReducedPriceOrInitialize = (
  prices: Array<any>,
  fields: Array<any>,
  currency?: Parameters<
    typeof buildTransformInputValueToCurrencyAmountStrFunc
  >[0],
) =>
  compact(
    prices.map((price: PaywallPrice) =>
      buildReducedPrice(price, currency, fields),
    ),
  );
